import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SignInResponse } from '../models/sign-in-response';
import { User } from '../models/user';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    isLoading$: Observable<boolean>;
    isLoadingSubject: BehaviorSubject<boolean>;
    private headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    currentUserSubject: BehaviorSubject<User | null>;
    currentUser$: Observable<User | null>;
    private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

    get currentUserValue(): User {
        return this.currentUserSubject.value!;
    }

    set currentUserValue(user: User) {
        this.currentUserSubject.next(user);
    }

    constructor(private http: HttpClient, private router: Router) {
        this.isLoadingSubject = new BehaviorSubject<boolean>(false);
        this.isLoading$ = this.isLoadingSubject.asObservable();
        this.currentUserSubject = new BehaviorSubject<User | null>(null);
        this.currentUser$ = this.currentUserSubject.asObservable();
    }

    decryptToken(token: string): Observable<SignInResponse> {
        this.isLoadingSubject.next(true);
        return this.http
            .post<any>(`${environment.api}/Auth/decrypt`, { authToken: token }, { headers: this.headers })
            .pipe(finalize(() => this.isLoadingSubject.next(false)));
    }

    getUserByToken(): Observable<User | undefined> {
        const auth = this.getAuthFromLocalStorage();
        if (!auth || !auth.accessToken) {
            return of(undefined);
        }

        this.isLoadingSubject.next(true);
        return this.http.get(`${environment.api}/Auth/me`).pipe(
            map((profile: any) => {
                if (profile) {
                    this.currentUserSubject = new BehaviorSubject<User | null>(profile);
                } else {
                    this.logout();
                }
                return profile;
            }),
            finalize(() => this.isLoadingSubject.next(false))
        );
    }

    refreshToken(): Observable<SignInResponse> {
        return this.http.post<SignInResponse>(
            `${environment.api}/auth/refresh-token`,
            {
                headers: this.headers,
            }
        );
    }

    setAuthFromLocalStorage(auth: SignInResponse): boolean {
        // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
        if (auth && auth.accessToken) {
          localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
          return true;
        }
        return false;
      }

    private getAuthFromLocalStorage(): SignInResponse | undefined {
        try {
            const authData = JSON.parse(localStorage.getItem(this.authLocalStorageToken)!);
            return authData;
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }

    logout() {
        localStorage.removeItem(this.authLocalStorageToken);
        window.location.replace(`${environment.portal}/logout`);
    }
}